import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import * as api from '../api/loginApi';
import { useLoginFlow } from '../contexts/LoginFlowContext';
import type { LoginStep, SsoProvider } from '../types/login';

import { useTurnstile } from './useTurnstile';

export const useLogin = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { returnUrl } = useLoginFlow();
    const { getToken: getTurnstileToken, resetWidget: resetTurnstile } = useTurnstile(
        'turnstile-widget-login',
        'login',
    );

    const [email, setEmail] = useState('');
    const [step, setStep] = useState<LoginStep>('email');
    const [showPassword, setShowPassword] = useState(false);
    const [ssoProviders, setSsoProviders] = useState<SsoProvider[]>([]);
    const [loginError, setLoginError] = useState<string | undefined>();

    // -----------------------------------------------------------------------
    // Fetch login providers for the entered email
    // -----------------------------------------------------------------------

    const providersMutation = useMutation({
        mutationFn: (emailAddress: string) => api.getLoginProviders(emailAddress, returnUrl),
        onSuccess: (data) => {
            // data is null when server returned a redirect (SSO)
            if (!data) return;

            setSsoProviders(data.ssoProviders ?? []);
            setShowPassword(data.password ?? true);

            if ((data.ssoProviders?.length ?? 0) > 0 || data.password === false) {
                setStep('providers');
            } else {
                setStep('password');
            }
        },
    });

    // -----------------------------------------------------------------------
    // Login with email + password
    // -----------------------------------------------------------------------

    const loginMutation = useMutation({
        mutationFn: async (password: string) => {
            const turnstileToken = getTurnstileToken();
            return api.postLogin({
                email,
                password,
                turnstileToken,
                returnUrl,
            });
        },
        onSuccess: (data) => {
            if (data.success) {
                if (data.requireVerification) {
                    navigate('/two-factor');
                } else if (data.returnUrl) {
                    window.location.href = data.returnUrl;
                }
            } else {
                resetTurnstile();
                setLoginError(
                    data.errorMessage ??
                        intl.formatMessage({
                            id: 'login.error.loginFailed',
                            defaultMessage: 'Login failed',
                        }),
                );
            }
        },
    });

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    const submitEmail = (emailAddress: string) => {
        setEmail(emailAddress);
        setLoginError(undefined);
        providersMutation.mutate(emailAddress);
    };

    const submitPassword = (password: string) => {
        setLoginError(undefined);
        loginMutation.mutate(password);
    };

    const switchAccount = () => {
        setStep('email');
        setSsoProviders([]);
        setShowPassword(false);
        setLoginError(undefined);
    };

    const goToPasswordStep = () => {
        setStep('password');
    };

    return {
        email,
        step,
        showPassword,
        ssoProviders,
        isLoadingProviders: providersMutation.isPending,
        providersError: providersMutation.error?.message,
        isLoggingIn: loginMutation.isPending,
        loginError: loginError ?? loginMutation.error?.message,
        submitEmail,
        submitPassword,
        switchAccount,
        goToPasswordStep,
    };
};
